import React, { ReactNode, SyntheticEvent, useState } from 'react';
import classNames from 'classnames';
import { useTheme } from '@components/theme';
import Caret from '@components/shared/images/atoms/Caret/Caret';
import AiSparkleIcon from '@components/shared/banners/AIHDABanner/ai-sparkle.svg';
import styles from './HomeDesignPackageCard.module.scss';

export type HomeDesignPackageCardVariant = 'terracotta' | 'navy';

interface IHomeDesignPackageCardProps {
  title: ReactNode;
  eyebrowPill: string;
  description: string;
  cornerBadge?: {
    label: ReactNode;
    variant: 'terracotta' | 'lavender';
  };
  price: ReactNode;
  priceCaption?: string;
  ctaLabel: string;
  ctaHref?: string;
  ctaTarget?: '_blank' | '_self';
  ctaOnClick?: (e: SyntheticEvent) => void;
  variant: HomeDesignPackageCardVariant;
  showSparkleIcon?: boolean;
  locations?: string[];
  dataTest?: string;
}

export default function HomeDesignPackageCard({
  title,
  eyebrowPill,
  description,
  cornerBadge,
  price,
  priceCaption,
  ctaLabel,
  ctaHref,
  ctaTarget,
  ctaOnClick,
  variant,
  showSparkleIcon = false,
  locations,
  dataTest,
}: IHomeDesignPackageCardProps) {
  const theme = useTheme();
  const [locationsExpanded, setLocationsExpanded] = useState(false);

  const ctaClassName = classNames(
    theme.styles.Button,
    variant === 'terracotta' ? theme.styles.Primary : null,
    styles.CTA
  );

  return (
    <article className={styles.Card} data-test={dataTest}>
      {cornerBadge && (
        <div
          className={classNames(
            styles.CornerBadge,
            styles[`CornerBadge_${cornerBadge.variant}`]
          )}
          aria-hidden="true"
        >
          <svg
            viewBox="0 0 127 127"
            xmlns="http://www.w3.org/2000/svg"
            className={styles.CornerBadgeShape}
          >
            <path d="M0 0 H127 V127 A127 127 0 0 1 0 0 Z" />
          </svg>
          <span className={styles.CornerBadgeLabel}>{cornerBadge.label}</span>
        </div>
      )}

      <h3 className={styles.Title}>
        {title}
        {showSparkleIcon && (
          <span className={styles.SparkleIcon} aria-hidden="true">
            <AiSparkleIcon />
          </span>
        )}
      </h3>

      <div
        className={classNames(
          styles.EyebrowPill,
          styles[`EyebrowPill_${variant}`]
        )}
      >
        {eyebrowPill}
      </div>

      <p className={styles.Description}>{description}</p>

      <div className={styles.PriceBlock}>
        {price}
        {priceCaption && (
          <p className={styles.PriceCaption}>{priceCaption}</p>
        )}
      </div>

      {ctaHref ? (
        <a
          href={ctaHref}
          target={ctaTarget}
          rel={ctaTarget === '_blank' ? 'noopener noreferrer' : undefined}
          className={ctaClassName}
          onClick={ctaOnClick}
        >
          {ctaLabel}
        </a>
      ) : (
        <button
          type="button"
          className={ctaClassName}
          onClick={ctaOnClick}
        >
          {ctaLabel}
        </button>
      )}

      <div className={styles.Locations} aria-hidden={!locations || locations.length === 0}>
        {locations && locations.length > 0 && (
          <>
            <button
              type="button"
              className={styles.LocationsToggle}
              onClick={() => setLocationsExpanded((v) => !v)}
              aria-expanded={locationsExpanded}
            >
              Eligible Zip Codes In:
              <Caret
                className={classNames(styles.LocationsCaret, {
                  [styles.LocationsCaretOpen]: locationsExpanded,
                })}
              />
            </button>
            {locationsExpanded && (
              <p className={styles.LocationsList}>{locations.join(', ')}</p>
            )}
          </>
        )}
      </div>
    </article>
  );
}
